import { useState, useEffect } from 'react';
import { AssetType, AssignedAlarmTag, AssignedTag } from 'src/types/assets';
import { STATE } from '../config';
import { useWebSockets } from 'src/contexts/webSocketsContext/websockets.context';

type usePumpDataArgumentsTypes = {
  asset: AssetType;
};

export function useAssetStateData({ asset }: usePumpDataArgumentsTypes) {
  const [assetState, setAssetState] = useState<string | null>(null);
  const [assetActiveAlarm, setAssetActiveAlarm] = useState<number | null>(null);
  const [isRunning, setIsRunning] = useState<boolean | null>(null);

  const assetAssignedTag: AssignedTag | undefined = asset?.assignedTags?.find(
    (tag) => tag?.tagName?.toLowerCase() === STATE
  );

  const { messages, assetStateMessages } = useWebSockets();

  const gatewayIds = [
    ...(asset?.assignedAlarmTags?.map((item) => item.assignedAlarmGatewayId).filter(Boolean) || []),

    ...(asset?.assignedTags?.map((item) => item.assignedGatewayId).filter(Boolean) || []),
  ];

  const findAsset = assetStateMessages?.find((item: any) => item?.data?.assetId === asset?.id);

  const getGatewayInputs = () => {
    const gatewayId = assetAssignedTag?.assignedGatewayId;

    if (!messages) return undefined;

    if (Array.isArray(messages)) {
      if (!gatewayId) return undefined;
      const matchedMessage = messages.find((msg: any) => gatewayIds?.includes(msg?.data?.sourceId));
      return matchedMessage?.data?.data;
    }

    if (messages?.data?.data) {
      if (gatewayId) {
        if (gatewayIds.includes(messages?.data?.sourceId)) {
          return messages.data.data;
        }
        return undefined;
      }
      return messages.data.data;
    }

    return undefined;
  };

  const gatewayInputs = getGatewayInputs();

  const findInput = gatewayInputs?.find(
    (input: { id: string | undefined }) => input.id === assetAssignedTag?.assignedTagId
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filteredAssignedAlarmTags: AssignedAlarmTag[] =
    asset?.assignedAlarmTags?.filter((tag) => tag?.assignedAlarmTagId) ?? [];

  useEffect(() => {
    if (!asset) {
      setAssetState(null);
      setIsRunning(null);
      return;
    }

    if (!findAsset) return;

    const currentState = findAsset?.data?.event?.currentState;

    if (currentState === null || currentState === undefined) {
      setAssetState('Undefined');
      setIsRunning(false);
      return;
    }

    setAssetState(currentState.label || 'Undefined');
    setIsRunning(currentState.isOperational ?? false);
  }, [findAsset, asset]);

  useEffect(() => {
    if (!asset) {
      setAssetActiveAlarm(null);
      return;
    }

    if (filteredAssignedAlarmTags?.length > 0) {
      if (!gatewayInputs) {
        return;
      }
      const hasActiveAlarms = filteredAssignedAlarmTags.some(
        (assignedAlarmTag: AssignedAlarmTag) => {
          const findInput = gatewayInputs?.find(
            (input: { id: string | undefined }) => input.id === assignedAlarmTag.assignedAlarmTagId
          );

          return Number(findInput?.value) > 0;
        }
      );

      setAssetActiveAlarm(hasActiveAlarms ? 1 : 0);
    }
  }, [
    assetAssignedTag?.assignedGatewayId,
    filteredAssignedAlarmTags,
    findInput,
    gatewayInputs,
    asset,
  ]);

  return { assetState, assetActiveAlarm, isRunningLive: isRunning };
}
